import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  IconButton
} from '@mui/material';
import {
  Google as GoogleIcon,
  Apple as AppleIcon,
  Facebook as FacebookIcon,
  Visibility,
  VisibilityOff,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon
} from '@mui/icons-material';
import { APP_NAME } from '../constants';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { mode, toggleTheme } = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // Add state to pass mustChangePassword to dashboard
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    username: '',
    password: ''
  });

  const handleLoginChange = (e) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(loginForm.username, loginForm.password);
    
    if (result.success) {
      if (result.must_change_password) {
        setMustChangePassword(true);
      }
      navigate('/dashboard', { state: { mustChangePassword: result.must_change_password } });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };



  const handleSSO = (provider) => {
    setError(`${provider} SSO is not implemented yet`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: mode === 'light' ? '#f5f5f5' : '#121212',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography component="h1" variant="h4">
                {APP_NAME}
              </Typography>
              <IconButton onClick={toggleTheme} color="inherit">
                {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
              </IconButton>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLoginSubmit} sx={{ mt: 1 }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={loginForm.username}
                  onChange={handleLoginChange}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Box>

            <Divider sx={{ my: 2 }}>OR</Divider>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={() => handleSSO('Google')}
                sx={{ flex: 1 }}
              >
                Google
              </Button>
              <Button
                variant="outlined"
                startIcon={<AppleIcon />}
                onClick={() => handleSSO('Apple')}
                sx={{ flex: 1 }}
              >
                Apple
              </Button>
              <Button
                variant="outlined"
                startIcon={<FacebookIcon />}
                onClick={() => handleSSO('Facebook')}
                sx={{ flex: 1 }}
              >
                Facebook
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
      <Box sx={{ 
        width: '100%', 
        textAlign: 'center', 
        py: 2, 
        color: mode === 'light' ? 'text.secondary' : 'text.primary',
        fontSize: '0.95em', 
        borderTop: 1, 
        borderColor: 'divider', 
        bgcolor: mode === 'light' ? 'background.paper' : 'background.default',
        position: 'fixed', 
        bottom: 0, 
        left: 0 
      }}>
        Copyright © 2025 Rajida Holdings (Pvt) Ltd. All Rights Reserved.
      </Box>
    </Box>
  );
};

export default Login; 